/**
 * Capacitor App plugin wrapper — Phase 7, incrément 7.2c.
 *
 * Fin wrapper autour de `@capacitor/app` pour l'évènement
 * `appUrlOpen` (deep-links). Consommé via le runtime `getPlugin('App')`
 * (cf. `lib/capacitor/index.ts`) → **no-op gracieux en web** : si
 * l'app ne tourne pas en natif ou si le plugin `App` n'est pas
 * disponible, `onAppUrlOpen` retourne une fonction de désabonnement
 * inerte.
 *
 * Le `backButton` Android (autre évènement du plugin App) sera câblé
 * en 7.3.
 */
import { getPlugin } from './index.js';

/** Subset du plugin `App` Capacitor consommé ici. */
interface AppPlugin {
  addListener(
    eventName: 'appUrlOpen',
    listener: (data: { url: string }) => void,
  ): Promise<PluginListenerHandle> | PluginListenerHandle;
  addListener(
    eventName: 'backButton',
    listener: (data: { canGoBack: boolean }) => void,
  ): Promise<PluginListenerHandle> | PluginListenerHandle;
}

interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

/**
 * S'abonne à l'ouverture de l'app via un lien (deep-link). `handler`
 * reçoit l'URL brute fournie par l'OS. Retourne une fonction de
 * désabonnement. No-op (désabonnement inerte) en web.
 */
export function onAppUrlOpen(handler: (url: string) => void): () => void {
  const plugin = getPlugin<AppPlugin>('App');
  if (!plugin) {
    return () => {
      /* no-op : pas de plugin natif (web) → rien à désabonner. */
    };
  }

  let handle: PluginListenerHandle | undefined;
  // `addListener` peut retourner une Promise<handle> (Capacitor 4+) ou
  // un handle direct selon le runtime — on normalise via Promise.resolve.
  void Promise.resolve(plugin.addListener('appUrlOpen', (data) => handler(data.url)))
    .then((h) => {
      handle = h;
    })
    .catch(() => {
      /* abonnement impossible : on reste silencieux (no-op). */
    });

  return () => {
    void handle?.remove();
  };
}

/**
 * S'abonne au bouton retour matériel Android (`backButton`). `handler`
 * reçoit `canGoBack` (vrai si la WebView a un historique). Retourne une
 * fonction de désabonnement. No-op (inerte) en web et sur iOS (pas de
 * bouton retour matériel).
 */
export function onBackButton(handler: (canGoBack: boolean) => void): () => void {
  const plugin = getPlugin<AppPlugin>('App');
  if (!plugin) {
    return () => {
      /* no-op : pas de plugin natif (web/iOS) → rien à désabonner. */
    };
  }

  let handle: PluginListenerHandle | undefined;
  void Promise.resolve(plugin.addListener('backButton', (data) => handler(data.canGoBack)))
    .then((h) => {
      handle = h;
    })
    .catch(() => {
      /* abonnement impossible : on reste silencieux (no-op). */
    });

  return () => {
    void handle?.remove();
  };
}
